import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { LogIn, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBeta } from "../context/BetaContext";
import { Button } from "../components/ui/button";

// Iter-158.12.12 — extensible department context.
// Department tokens minted server-side already pass a strict
// `[a-z0-9][a-z0-9-]{0,31}` sanitiser; this map is the FE's
// enumeration of dept tokens that have a real destination workspace.
// Future departments (art / costume / vfx) drop in here and the
// URL/join/route chain works for them without another invitation
// architecture. Absent or unknown token = fall through to the
// project-level department picker (safe default).
const _BETA_DEPT_LANDING = {
    hm: (pid) => `/beta/hm-app/projects/${pid}`,
};

/**
 * Iter-158.12.2 — Beta workspace join page.
 *
 * Reached from an invite URL of the form `/join/beta/{invite_code}`.
 * Calls the beta-only `POST /api/beta/projects/join` endpoint so the
 * user never accidentally joins a legacy project through this flow.
 *
 * Iter-158.12.12 — honours an optional `?dept=<token>` query param.
 * When the invite was minted from inside a department workspace
 * (see ProjectSettings.jsx), the query param carries that context
 * through login and this page lands the invitee directly in that
 * department's workspace. Project-level invitations (no dept)
 * continue to land at the production dept picker.
 */
export default function JoinBetaProject() {
    const { code } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { status } = useAuth();
    const { refresh: refreshBeta } = useBeta();
    const [pageStatus, setPageStatus] = useState("joining");
    const [error, setError] = useState(null);

    useEffect(() => {
        if (status === "loading") return;
        let cancelled = false;
        (async () => {
            try {
                if (status !== "authed") {
                    // Preserve BOTH the beta code AND the ?dept
                    // param through the login round-trip. Any query
                    // string we recieved gets carried verbatim to
                    // AuthPage which honours useSearchParams(next).
                    const rawQs = searchParams.toString();
                    const nextPath = `/join/beta/${encodeURIComponent(code)}${
                        rawQs ? `?${rawQs}` : ""
                    }`;
                    // Iter-158.13.2.6 — explicit invite intent marker.
                    // Belt-and-braces to the ?next= chain: even if a
                    // downstream redirect (BetaGate flash / catch-all
                    // `*` route / stale token bounce) drops us at "/"
                    // BEFORE JoinBetaProject gets a chance to complete
                    // the join, MyProjects sees this marker and
                    // resumes the invite journey instead of auto-
                    // jumping the user into their pre-existing sole
                    // department (the BT9 hijack bug). Cleared in both
                    // success + error branches below.
                    try {
                        sessionStorage.setItem(
                            "osf_pending_invite_target",
                            nextPath,
                        );
                    } catch { /* private mode: fall back to next= only */ }
                    navigate(`/login?next=${encodeURIComponent(nextPath)}`, {
                        replace: true,
                    });
                    return;
                }
                const { data } = await api.post("/beta/projects/join", {
                    invite_code: code,
                });
                if (cancelled) return;
                setPageStatus("joined");
                // Iter-158.13.2.6 — invite journey completed; clear the
                // explicit-intent marker so a subsequent manual visit
                // to "/" behaves normally.
                try {
                    sessionStorage.removeItem("osf_pending_invite_target");
                } catch { /* ignore */ }
                try {
                    await refreshBeta();
                } catch {
                    /* non-fatal; BetaGate will retry on next mount */
                }
                if (cancelled) return;
                const pid = data?.id;
                if (!pid) {
                    navigate("/", { replace: true });
                    return;
                }
                // Iter-158.13.2.3 — server-authoritative landing. The
                // backend derives landing_url from the invite record
                // (per-invitation dept context), so a `?dept=hm` URL
                // param that the project-level invite_code cannot
                // back is IGNORED. Prevents the Copy-Link
                // project_not_found bug where a project-wide code
                // combined with `?dept=hm` sent the invitee to
                // /beta/hm-app/projects/{pid} — a surface the backend
                // then 404'd because no per-invitation record added
                // them to hair_makeup.members[]. Falls back to the
                // (legacy) URL param map only when the backend
                // response is missing landing_url (older BE).
                if (typeof data.landing_url === "string" && data.landing_url) {
                    navigate(data.landing_url, { replace: true });
                    return;
                }
                const dept = (searchParams.get("dept") || "").toLowerCase();
                const landing = _BETA_DEPT_LANDING[dept];
                const target = landing ? landing(pid) : `/beta/projects/${pid}`;
                navigate(target, { replace: true });
            } catch (err) {
                if (cancelled) return;
                // Iter-158.13.2.6 — invite journey terminated (invalid
                // code / not-authorised / server error). Clear the
                // explicit-intent marker so the user's next visit to
                // "/" is not treated as still-in-progress and does not
                // get bounced back into a dead invite URL.
                try {
                    sessionStorage.removeItem("osf_pending_invite_target");
                } catch { /* ignore */ }
                const respStatus = err?.response?.status;
                const detail = err?.response?.data?.detail;
                let msg = formatApiError(err);
                if (respStatus === 404 || detail === "Invalid invite code") {
                    msg = "This invite link is no longer valid. Ask the project owner to send you a fresh invitation.";
                } else if (detail === "beta_not_authorized") {
                    msg = "We couldn't add you to this project. Please refresh the page and try again — if it keeps failing, contact the person who invited you.";
                } else if (!msg) {
                    msg = "Unable to join this project.";
                }
                setError(msg);
                setPageStatus("error");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [code, status, navigate, refreshBeta, searchParams]);

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
            <div className="w-full max-w-md space-y-6" data-testid="join-beta-project-page">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                </Link>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 space-y-4">
                    <div className="flex items-center gap-3">
                        <LogIn className="w-5 h-5 text-amber-400" />
                        <h1 className="text-xl font-semibold">Joining project</h1>
                    </div>
                    {pageStatus === "joining" && (
                        <p className="text-slate-300" data-testid="join-beta-status-joining">
                            Adding you to the project…
                        </p>
                    )}
                    {pageStatus === "joined" && (
                        <p className="text-emerald-300" data-testid="join-beta-status-joined">
                            You&rsquo;re in. Opening your workspace…
                        </p>
                    )}
                    {pageStatus === "error" && (
                        <>
                            <p className="text-rose-300" data-testid="join-beta-status-error">
                                {error}
                            </p>
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => navigate("/", { replace: true })}
                                    variant="secondary"
                                    data-testid="join-beta-back-home-btn"
                                >
                                    Back to my projects
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
